import { getApiUrl } from "../../services/apiConfig.js";
/* Teacher management only. Uses existing institution styles and endpoints; no student/task/report UI. */
(function () {
  "use strict";

  const node = (tag, cls, text) => {
    const createElementResult = document.createElement(tag);
    if (cls) createElementResult.className = cls;
    if (text !== undefined) createElementResult.textContent = text;
    return createElementResult;
  };
  function mount(root, session, institutionId, options = {}) {
    const house = Number(institutionId),
      role = session?.tipo;
    if (!house || !["admin", "instituicao"].includes(role)) {
      root.textContent = "A gestão de professores é da instituição.";
      return () => root.replaceChildren();
    }
    if (!document.querySelector("link[data-institutions]")) {
      const link = node("link");
      link.rel = "stylesheet";
      link.href = "/static/css/institutions-v1.css?v=scope-7918dab5dfba";
      link.dataset.institutions = "1";
      document.head.append(link);
    }
    let alive = true,
      generation = 0,
      search = "";
    const controllers = new Set();
    root.classList.add("di-root", "dp-management");
    const button = (text, index, cls = "di-button") => {
      const nodeResult = node("button", cls, text);
      nodeResult.type = "button";
      nodeResult.onclick = index;
      return nodeResult;
    };
    const notice = (text) => {
      if (alive) options.notify?.(text);
    };
    async function api(resource, method = "GET", data = null, query = {}) {
      let current;
      try {
        current = JSON.parse(sessionStorage.getItem("auth_session") || "null");
      } catch {}
      if (
        current &&
        (current.tipo !== role ||
          String(current.data?.id) !== String(session.data?.id))
      )
        throw Error("A conta mudou. Entre novamente para continuar.");
      const headers = {
        "Content-Type": "application/json",
      };
      if (role === "admin") {
        if (session.data?.token) headers["X-Admin-Token"] = session.data.token;
        if (session.data?.learning_admin_token)
          headers["X-Learning-Token"] = session.data.learning_admin_token;
      } else headers.Authorization = "Bearer " + session.data.access_token;
      const controller = new AbortController();
      controllers.add(controller);
      try {
        const response = await fetch(
          getApiUrl(
            "/api/institutions.php?" +
              new URLSearchParams({
                resource,
                id: house,
                ...query,
              }),
          ),
          {
            method,
            headers,
            body: data ? JSON.stringify(data) : undefined,
            signal: controller.signal,
            credentials: "same-origin",
            cache: "no-store",
          },
        );
        const json = await response.json();
        if (!response.ok || json.status !== "success")
          throw Error(json.message || "Não foi possível concluir.");
        return json.data;
      } finally {
        controllers.delete(controller);
      }
    }
    function status(container, text) {
      const nodeResult = node("p", "di-notice", text);
      nodeResult.setAttribute("role", "status");
      container.append(nodeResult);
      notice(text);
    }
    async function list() {
      const gen = ++generation;
      root.replaceChildren();
      const tools = node("div", "di-tools"),
        find = node("input", "di-search");
      find.type = "search";
      find.placeholder = "Pesquisar professor";
      find.setAttribute("aria-label", "Pesquisar professor");
      find.value = search;
      tools.append(
        find,
        button("Cadastrar professor", () => form(null), "di-button di-primary"),
      );
      root.append(tools);
      const area = node("div", "di-panel di-table-wrap"),
        loading = node("p", "di-muted", "Carregando professores…");
      area.append(loading);
      root.append(area);
      try {
        const rows = await api("teachers");
        if (!alive || gen !== generation) return;
        loading.remove();
        function paint() {
          area.replaceChildren();
          const toLocaleLowerCaseResult = search.toLocaleLowerCase("pt-BR"),
            matches = rows.filter((row) =>
              (row.nome + " " + row.email)
                .toLocaleLowerCase("pt-BR")
                .includes(toLocaleLowerCaseResult),
            );
          const table = node("table", "di-table"),
            head = node("thead"),
            headRow = node("tr");
          for (const title of [
            "Professor",
            "E-mail ou apelido",
            "Situação",
            "Ações",
          ]) {
            const nodeResult = node("th", null, title);
            nodeResult.scope = "col";
            headRow.append(nodeResult);
          }
          head.append(headRow);
          table.append(head);
          const body = node("tbody");
          for (const teacher of matches) {
            const nodeResult = node("tr");
            nodeResult.dataset.id = teacher.id;
            nodeResult.append(
              node("td", null, teacher.nome),
              node("td", null, teacher.email),
              node(
                "td",
                null,
                Number(teacher.active) && Number(teacher.ativo)
                  ? "Ativo"
                  : "Desativado",
              ),
            );
            const actions = node("td"),
              group = node("div", "di-list-actions");
            group.append(
              button("Editar", () => form(teacher)),
              button(Number(teacher.active) ? "Desativar" : "Reativar", () =>
                confirmStatus(teacher),
              ),
            );
            actions.append(group);
            nodeResult.append(actions);
            body.append(nodeResult);
          }
          table.append(body);
          area.append(table);
          if (!matches.length)
            area.append(node("p", "di-muted", "Nenhum professor encontrado."));
        }
        find.oninput = () => {
          search = find.value;
          paint();
        };
        paint();
      } catch (error) {
        if (alive && gen === generation && error.name !== "AbortError")
          status(area, error.message);
      }
    }
    function confirmStatus(teacher) {
      const gen = generation,
        box = node("section", "di-confirm"),
        active = !Number(teacher.active);
      box.append(
        node(
          "p",
          null,
          (active ? "Reativar" : "Desativar") +
            " o acesso de " +
            teacher.nome +
            "?",
        ),
      );
      const save = button(active ? "Reativar" : "Desativar", async () => {
        save.disabled = true;
        try {
          await api("teacher-status", "POST", {
            account_id: teacher.id,
            active: !!active,
          });
          if (alive && gen === generation) {
            await list();
            notice("Acesso atualizado.");
          }
        } catch (error) {
          if (alive && gen === generation) status(box, error.message);
        } finally {
          save.disabled = false;
        }
      });
      box.append(
        save,
        button("Cancelar", () => box.remove()),
      );
      root.prepend(box);
    }
    async function form(teacher) {
      const gen = ++generation;
      root.replaceChildren();
      const panel = node("section", "di-panel"),
        heading = node(
          "h2",
          null,
          teacher ? "Editar professor" : "Cadastrar professor",
        );
      panel.append(heading);
      root.append(panel);
      try {
        if (teacher)
          teacher = await api("teacher", "GET", null, {
            account_id: teacher.id,
          });
        if (!alive || gen !== generation) return;
        const form = node("form", "di-form");
        function field(label, name, type, value, required) {
          const wrap = node("label", "di-field", label),
            input = node("input");
          input.name = name;
          input.type = type;
          input.value = value ?? "";
          input.required = required;
          input.setAttribute("aria-label", label);
          input.autocomplete =
            type === "password"
              ? "new-password"
              : name === "email"
                ? "username"
                : "off";
          input.maxLength =
            type === "password" ? 72 : name === "email" ? 254 : 100;
          if (type === "password") {
            input.minLength = 6;
            input.placeholder = teacher
              ? "Preencha para redefinir"
              : "Mínimo de 6 caracteres";
          }
          wrap.append(input);
          form.append(wrap);
          return input;
        }
        field("Nome", "name", "text", teacher?.nome, true);
        field("E-mail ou apelido", "email", "text", teacher?.email, true);
        const passwordInput = field(
          "Senha",
          "password",
          "password",
          "",
          !teacher,
        );
        passwordInput.oninput = () => passwordInput.setCustomValidity("");
        if (teacher) {
          const label = node("label", "di-field", "Acesso ativo"),
            input = node("input");
          input.type = "checkbox";
          input.name = "active";
          input.checked = !!Number(teacher.active);
          input.setAttribute("aria-label", "Acesso ativo");
          label.append(input);
          form.append(label);
        }
        const save = node("button", "di-button di-primary", "Salvar");
        save.type = "submit";
        form.append(save, button("Cancelar", list));
        panel.append(form);
        form.onsubmit = async (event) => {
          event.preventDefault();
          const password = form.elements.password.value;
          if (password && Array.from(password).length < 6) {
            form.elements.password.setCustomValidity(
              "A senha deve ter pelo menos 6 caracteres.",
            );
            form.reportValidity();
            return;
          }
          save.disabled = true;
          const data = Object.fromEntries(new FormData(form));
          if (teacher) data.active = form.elements.active.checked;
          try {
            await api(
              teacher ? "teacher" : "teachers",
              teacher ? "PUT" : "POST",
              data,
              teacher
                ? {
                    account_id: teacher.id,
                  }
                : {},
            );
            if (alive && gen === generation) {
              await list();
              notice("Professor salvo.");
            }
          } catch (error) {
            if (alive && gen === generation) status(panel, error.message);
          } finally {
            save.disabled = false;
          }
        };
      } catch (error) {
        if (alive && gen === generation && error.name !== "AbortError")
          status(panel, error.message);
      }
    }
    list();
    return () => {
      alive = false;
      ++generation;
      for (const controller of controllers) controller.abort();
      root.replaceChildren();
      root.classList.remove("di-root", "dp-management");
    };
  }
  window.DMInstitutionTeachers = Object.freeze({
    mount,
  });
})();
